import styles from 'css/src/components/common/BrandKitDropdown.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import classNames from 'classnames';

import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import { getAllBrandKits, getBrandKitById, getBrandKitColorsInHex } from 'js/src/data/brand-kits';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

const NONE_ID = 'brand-kit-none';
const SEARCH_ID = 'brand-kit-search';

type SearchItemProps = {
    value: string;
    onChange: (value: string) => void;
};

const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
};

const BrandKitSearchItem = React.memo(function BrandKitSearchItem({
    value,
    onChange,
}: SearchItemProps) {
    return (
        <div className={styles['search-item']} onClick={stopPropagation}>
            <i aria-hidden="true" className="material-symbols-outlined">
                search
            </i>
            <input
                type="text"
                className={styles['search-input']}
                placeholder="Search brand kits"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={stopPropagation}
                onKeyDown={stopPropagation}
                autoFocus
            />
        </div>
    );
});

type BrandKitDropdownProps = {
    brandKitIds: Immutable.List<number>;
    selectedBrandKitId: number | null;
    onSelect: (brandKitId: number | null) => void;
    placeholder?: string;
    className?: string;
};

type ToggleProps = {
    onClick: VoidFunction;
    'aria-expanded'?: boolean | 'true' | 'false';
    ref?: React.Ref<HTMLButtonElement>;
    selectedBrandKitId: number | null;
    placeholder: string;
};

type Colors = Immutable.List<string> | string[];

const ColorDots: React.FC<{ colors: Colors }> = ({ colors }) => {
    const list = Immutable.List(colors).slice(0, 5);
    if (list.size === 0) return null;
    return (
        <span className={styles['color-dots']}>
            {list.map((hex, i) => (
                <span key={i} className={styles['color-dot']} style={{ backgroundColor: hex }} />
            ))}
        </span>
    );
};

const WatermarkThumb: React.FC<{ brandKitId: number }> = ({ brandKitId }) => {
    const brandKit = useSelector((state: RootState) => getBrandKitById(state, brandKitId));
    const watermarkDetails = brandKit?.get('watermarkDetails');
    if (!watermarkDetails || watermarkDetails.size === 0) {
        return <div className={styles['watermark-thumb-placeholder']} aria-hidden="true" />;
    }
    const first = watermarkDetails.first();
    const url = first?.get('thumbnail_url');
    return (
        <div className={styles['watermark-thumb']}>
            <img src={url as string} alt="" />
        </div>
    );
};

const BrandKitRow: React.FC<{ brandKitId: number }> = ({ brandKitId }) => {
    const brandKit = useSelector((state: RootState) => getBrandKitById(state, brandKitId));
    const colors = useSelector((state: RootState) => getBrandKitColorsInHex(state, brandKitId));
    const name = brandKit ? brandKit.get('name') : '';
    return (
        <>
            <WatermarkThumb brandKitId={brandKitId} />
            <span className={styles['row-text']}>
                <span className={styles['row-name']}>{name}</span>
                <ColorDots colors={colors} />
            </span>
        </>
    );
};

const BrandKitToggle = React.memo(function BrandKitToggle({
    onClick,
    'aria-expanded': ariaExpanded,
    ref,
    selectedBrandKitId,
    placeholder,
}: ToggleProps) {
    return (
        <button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-expanded={ariaExpanded}
            className={styles['brand-kit-toggle']}
            data-testid="brand-kit-dropdown-toggle"
        >
            {selectedBrandKitId != null ? (
                <BrandKitRow brandKitId={selectedBrandKitId} />
            ) : (
                <span className={styles['placeholder']}>{placeholder}</span>
            )}
            <i aria-hidden="true" className={`material-symbols-outlined ${styles['caret']}`}>
                expand_more
            </i>
        </button>
    );
});

const BrandKitDropdown: React.FC<BrandKitDropdownProps> = ({
    brandKitIds,
    selectedBrandKitId,
    onSelect,
    placeholder = 'Select a brand kit',
    className,
}) => {
    const [query, setQuery] = React.useState('');
    const clearQuery = React.useCallback(() => setQuery(''), []);

    const allBrandKits = useSelector(getAllBrandKits);
    const namesById = React.useMemo(() => {
        const lookup: Record<number, string> = {};
        allBrandKits.forEach((brandKit) => {
            lookup[brandKit.get('id')] = brandKit.get('name');
        });
        return lookup;
    }, [allBrandKits]);

    const filteredBrandKitIds = React.useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) {
            return brandKitIds;
        }
        return brandKitIds.filter((id) => (namesById[id] ?? '').toLowerCase().includes(trimmed));
    }, [brandKitIds, namesById, query]);

    const itemPropsList = React.useMemo(() => {
        const items: Array<{
            id: string;
            text: string;
            onSelectData: { brandKitId: number | null };
            className?: string;
            'data-testid'?: string;
            children?: React.ReactNode;
            itemComponent?: React.ComponentType;
            value?: string;
            onChange?: (value: string) => void;
        }> = [
            {
                id: SEARCH_ID,
                text: '',
                onSelectData: { brandKitId: null },
                itemComponent: BrandKitSearchItem as unknown as React.ComponentType,
                value: query,
                onChange: setQuery,
            },
            {
                id: NONE_ID,
                text: 'None',
                onSelectData: { brandKitId: null },
                className: styles['none-item'],
            },
        ];
        filteredBrandKitIds.forEach((id) => {
            items.push({
                id: `brand-kit-${id}`,
                text: '',
                onSelectData: { brandKitId: id },
                className: styles['kit-item'],
                'data-testid': `brand-kit-option-${id}`,
                children: (
                    <span className={styles['item-content']}>
                        <BrandKitRow brandKitId={id} />
                    </span>
                ),
            });
        });
        return ImmutableFromJS(items) as Immutable.List<
            DropdownItemProp<{ brandKitId: number | null }>
        >;
    }, [filteredBrandKitIds, query]);

    const handleSelect = React.useCallback(
        (data: ImmutableMap<{ brandKitId: number | null }>) => {
            const value = data?.get?.('brandKitId') ?? null;
            onSelect(value);
        },
        [onSelect],
    );

    const toggleProps = React.useMemo(
        () =>
            Immutable.Map({
                selectedBrandKitId,
                placeholder,
            }) as Immutable.Collection<unknown, unknown>,
        [selectedBrandKitId, placeholder],
    );

    return (
        <div className={classNames(styles['brand-kit-dropdown'], className)}>
            <Dropdown
                ToggleComponent={BrandKitToggle}
                toggleProps={toggleProps}
                itemPropsList={itemPropsList}
                onSelect={handleSelect}
                afterClose={clearQuery}
                breakOverflow={true}
                limitMenuHeight={true}
            />
        </div>
    );
};

export default BrandKitDropdown;
